import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingCartContainer } from '@components/organisms/ShoppingCartContainer';
import { ConfirmProductCard } from '@components/molecules/ConfirmProductCard';
import { Card } from '@components/atoms/Card';
import { Typography } from '@components/atoms/Typography';
import { formatFiscalNumber, formatPhoneNumber, formatPrice } from '@utils/format';
import { Address, ProductWithAvailability, ShoppingCartTotalData } from '@types';
import { SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { useGetPickupSpotDetail } from '@services/useGetPickupSpotDetail';
import { useCreateOrder } from '@services/useCreateOrder';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { Button, ButtonMode } from '@components/atoms/Button';
import { useNotifications } from '@hooks/useNotifications';
import { useRedirectedOnBack } from '@hooks/useRedirectedOnBack';
import { ErrorMessages } from '@components/atoms/ErrorMessages';
import { useGetAllLockers } from '@services/useGetAllLockers';

const CURRENT_STEP = 5;

export interface ConfirmOrderProps {
    productsByMerchant: {
        merchant: string;
        products: ProductWithAvailability[];
    }[];
    billingAddress: Address;
    shippingAddress: Address;
    total: Omit<ShoppingCartTotalData, 'products'>;
}

export const ConfirmOrder: FC<ConfirmOrderProps> = ({ productsByMerchant, billingAddress, shippingAddress, total }) => {
    // if user accessed this page by going back on browser, redirect to initial shopping cart page
    useRedirectedOnBack(`/${SITE_URLS.shoppingCart}`);

    const { navigate } = useCustomNavigate();
    const { showDefaultNotification } = useNotifications();
    const { login, cart, pickupSpotDetail, allLockers, setCart, setCreatedReservations } = useData();
    const {
        request: requestCreateOrder,
        loading: loadingCreateOrder,
        errorMessage: errorMessageCreateOrder,
        lastResponseTime: lastCreateOrder,
    } = useCreateOrder();
    const { request: requestGetPickupSpotDetail, loading: loadingGetPickupSpotDetail } = useGetPickupSpotDetail();
    const { request: requestGetAllLockers, loading: loadingGetAllLockers } = useGetAllLockers();
    const { t } = useTranslation();

    const translations = {
        title: t('confirm-order-component.title'),
        billingInfoLabel: t('confirm-order-component.billing-info.label'),
        fiscalNumberLabel: t('confirm-order-component.fiscal-number.label'),
        shippingInfoLabel: t('confirm-order-component.shipping-info.label'),
        deliveryOptionsLabel: t('confirm-order-component.delivery-options.label'),
        deliveryOptionsError: t('confirm-order-component.delivery-options.error'),
        pickupAtStore: t('confirm-order-component.delivery-options.at-store-pickup'),
        paymentMethodLabel: t('confirm-order-component.payment-method.label'),
        payButton: t('confirm-order-component.pay.button', { price: formatPrice(total.total) }),
    };

    // if user is not logged in, he can't access this page
    useEffect(() => {
        if (login === null) {
            navigate(`/${SITE_URLS.login}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [login]);

    // get information about pickup spot
    useEffect(() => {
        if (cart?.data?.delivery?.shipToPickupSpotId) {
            requestGetPickupSpotDetail({ id: cart?.data?.delivery?.shipToPickupSpotId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart?.data?.delivery?.shipToPickupSpotId]);

    const selectedLocker = allLockers?.data?.find((locker) => `${locker.id}` === `${cart?.data?.delivery?.pickupAtLocker}`);

    useEffect(() => {
        if (allLockers === null && !loadingGetAllLockers) {
            requestGetAllLockers();
        }
    }, [cart?.data?.delivery?.pickupAtLocker]);

    const handleOnConfirmOrder = () => {
        requestCreateOrder();
    };

    useEffect(() => {
        if (lastCreateOrder) {
            if (errorMessageCreateOrder) {
                showDefaultNotification({ type: 'error', message: errorMessageCreateOrder });
            } else {
                // reset cart data
                setCart(null);
                // to know that the last checkout was of an order
                setCreatedReservations(null);
                navigate(`/${SITE_URLS.orderFeedback}`);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCreateOrder]);

    return (
        <ShoppingCartContainer
            step={CURRENT_STEP}
            title={translations.title}
            action={
                <div className="d-grid gap-3">
                    <Button
                        label={translations.payButton}
                        mode={ButtonMode.primary}
                        disabled={loadingCreateOrder}
                        onClick={handleOnConfirmOrder}
                    />
                </div>
            }
            total={{ ...total, products: [] }}>
            <>
                {/* products */}
                {productsByMerchant.map((merchantProducts, merchantIndex) =>
                    merchantProducts.products.map((product, productIndex) => (
                        <ConfirmProductCard
                            key={`merchant-${merchantIndex}-product-${productIndex}`}
                            name={product.name}
                            image={product.image}
                            merchant={merchantProducts.merchant}
                            price={product.price}
                            quantity={product.quantity || 0}
                        />
                    ))
                )}

                {/* order summary */}
                <Card classes="p-4">
                    <div className="row">
                        <div className="col-12 col-sm-6">
                            <Typography classes="mb-3" fontSize="16" fontWeight="900">
                                {translations.billingInfoLabel}
                            </Typography>
                            <Typography classes="mb-3" fontSize="16">
                                <>
                                    {billingAddress.name} {billingAddress.lastName || ''}
                                </>
                            </Typography>
                            {billingAddress.fiscalNumber && (
                                <Typography classes="mb-1" fontSize="14">
                                    {`${translations.fiscalNumberLabel}: ${formatFiscalNumber(billingAddress.fiscalNumber)}`}
                                </Typography>
                            )}
                            <Typography classes="mb-1" fontSize="14">
                                {billingAddress.address}
                            </Typography>
                            <Typography classes="mb-1" fontSize="14">
                                {billingAddress.postalCode}
                            </Typography>
                            <Typography classes="mb-1" fontSize="14">
                                {billingAddress.region}
                            </Typography>
                        </div>
                        <div className="d-md-none col-12 p-0">
                            <hr className="my-4" />
                        </div>
                        <div className="col-12 col-sm-6">
                            <Typography classes="mb-3" fontSize="16" fontWeight="900">
                                {translations.shippingInfoLabel}
                            </Typography>
                            {cart?.data?.delivery?.pickupAtStore ? (
                                translations.pickupAtStore
                            ) : cart?.data?.delivery?.shipToPickupSpotId ? (
                                <>
                                    {/* display selected pickup spot */}
                                    {!loadingGetPickupSpotDetail &&
                                        cart?.data?.delivery?.shipToPickupSpotId &&
                                        pickupSpotDetail?.data && (
                                            <>
                                                <Typography classes="mb-3" fontSize="16">
                                                    <>{pickupSpotDetail?.data?.name || ''}</>
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {pickupSpotDetail?.data?.addressLine1 +
                                                        ' ' +
                                                        pickupSpotDetail?.data?.addressLine2 +
                                                        ' ' +
                                                        pickupSpotDetail?.data?.streetNumber}
                                                </Typography>
                                                <Typography classes="mb-1" fontSize="14">
                                                    {`${pickupSpotDetail?.data?.postalCode} ${pickupSpotDetail?.data?.city}`}
                                                </Typography>
                                            </>
                                        )}
                                </>
                            ) : cart?.data?.delivery?.pickupAtLocker ? (
                                <>
                                    {/* display selected locker */}
                                    {selectedLocker ? (
                                        <>
                                            <Typography classes="mb-3" fontSize="16">
                                                <>{selectedLocker.name || ''}</>
                                            </Typography>
                                            <Typography classes="mb-1" fontSize="14">
                                                {selectedLocker.address}
                                            </Typography>
                                            <Typography classes="mb-1" fontSize="14">
                                                {selectedLocker.postalCode}
                                            </Typography>
                                            <Typography classes="mb-1" fontSize="14">
                                                {selectedLocker.location}
                                            </Typography>
                                        </>
                                    ) : (
                                        <ErrorMessages messages={[translations.deliveryOptionsError]} />
                                    )}
                                </>
                            ) : (
                                <>
                                    <Typography classes="mb-3" fontSize="16">
                                        <>
                                            {shippingAddress.name} {shippingAddress.lastName || ''}
                                        </>
                                    </Typography>
                                    {shippingAddress.fiscalNumber && (
                                        <Typography classes="mb-1" fontSize="14">
                                            {`${translations.fiscalNumberLabel}: ${formatFiscalNumber(shippingAddress.fiscalNumber)}`}
                                        </Typography>
                                    )}
                                    <Typography classes="mb-1" fontSize="14">
                                        {shippingAddress.address}
                                    </Typography>
                                    <Typography classes="mb-1" fontSize="14">
                                        {shippingAddress.postalCode}
                                    </Typography>
                                </>
                            )}
                        </div>
                    </div>
                    <hr className="my-4" />
                    <div className="row">
                        <div className="col-12 col-sm-6">
                            <Typography classes="mt-1 mb-3" fontSize="16" fontWeight="900">
                                {translations.paymentMethodLabel}
                            </Typography>
                            {cart?.data?.paymentMethod?.image?.src ? (
                                <img src={cart.data.paymentMethod.image.src} alt={cart.data.paymentMethod.name} />
                            ) : (
                                cart?.data?.paymentMethod?.name && <p>{cart.data.paymentMethod.name}</p>
                            )}

                            <div className="d-inline-block ms-3">
                                {cart?.data?.paymentMethod?.method === 'mbway' &&
                                    formatPhoneNumber(cart?.data?.paymentMethod.paymentPhoneNumber || '')}
                            </div>
                        </div>
                    </div>
                </Card>
            </>
        </ShoppingCartContainer>
    );
};
